import { normalizePortalLocale, type PortalLocale } from "./locale";

export type PublicExperienceCopy = {
  account: string;
  publicActions: string;
  portraitAlt: (name: string) => string;
  viewProfile: (name: string) => string;
  recommended: string;
  professional: string;
  serviceCount: (count: number) => string;
  noProviders: string;
  filteredEmpty: string;
  unpublishedEmpty: string;
  clearFilters: string;
  talkToConcierge: string;
  catalogSeoTitle: (businessName: string) => string;
  catalogSeoDescription: string;
  openAgenda: string;
  heroTitle: string;
  heroTitleEmphasis: string;
  heroDescription: string;
  heroNote: string;
  heroNoteEmphasis: string;
  directoryFilters: string;
  searchProvider: string;
  filterByService: string;
  filterByLocation: string;
  allServices: string;
  allLocations: string;
  directory: string;
  directoryHeading: string;
  searching: string;
  profileCount: (count: number) => string;
  localTimezone: string;
  securePayment: string;
  immediateConfirmation: string;
  concierge: string;
  conciergeDescription: string;
  footer: string;
  profileError: string;
  directoryLoadError: string;
  profileLoadError: string;
  backToDirectory: string;
  publicProfile: string;
  profileDefaultBio: string;
  scheduleIn: string;
  chooseFirst: string;
  whatToBook: string;
  selectServiceForSchedule: (name: string) => string;
  serviceDuration: (minutes: number) => string;
  activeWithoutServices: string;
  modality: string;
  whereToMeet: string;
  availabilityInLocalTimezone: string;
  chooseLocation: string;
  modalityConfirmedLater: string;
  yourSchedule: string;
  chooseService: string;
  chooseServiceForSlots: string;
  needHelp: string;
  conciergeKnowsProfile: string;
  conciergeProfileDescription: string;
};

export type CustomerCopy = {
  loadingRecurring: string;
  recurringTitle: string;
  recurringDescription: string;
  recurringServiceFallback: string;
  recurringInterval: (value: number) => string;
  recurringPayment: (value: string) => string;
  recurringLoadFailed: string;
  cancelOccurrenceConfirm: string;
  cancelFutureConfirm: string;
  occurrenceCancelled: string;
  futureCancelled: string;
  recurringCancelFailed: string;
  noSlotsForDate: string;
  slotsLoadFailed: string;
  selectSlot: string;
  occurrenceRescheduled: string;
  rescheduleFailed: string;
  cancelFuture: string;
  meeting: string;
  meetingAvailable: string;
  reschedule: string;
  cancel: string;
  findAnotherDate: string;
  searching: string;
  searchTimes: string;
  noAvailableTimes: string;
  saving: string;
  applyReschedule: string;
  status: (value: string) => string;
  privacyTitle: string;
  privacyDescription: string;
  refreshing: string;
  refresh: string;
  dataCopy: string;
  dataErasure: string;
  completed: string;
  download: string;
  noPrivacyRequests: string;
  privacyLoadFailed: string;
  paymentsTitle: string;
  paymentsDescription: string;
  payments: string;
  invoices: string;
  loading: string;
  transactions: string;
  noPayments: string;
  pending: string;
  noInvoices: string;
  paymentDetail: string;
  close: string;
  transactionCount: (count: number) => string;
  noTransactions: string;
  paymentDetailLoadFailed: string;
  workspaceGreeting: (name: string) => string;
  workspaceDescription: string;
  bookingFallback: string;
  workspaceLoadFailed: string;
  cancelBookingConfirm: string;
  bookingCancelled: string;
  bookingCancelFailed: string;
  profileUpdated: string;
  profileUpdateFailed: string;
  preferencesSaved: string;
  preferencesSaveFailed: string;
  privacyExportRequested: (id: string) => string;
  privacyExportFailed: string;
  privacyErasureConfirm: string;
  privacyErasureRequested: (id: string) => string;
  privacyErasureFailed: string;
  signOut: string;
  bookingsTitle: string;
  bookingServiceFallback: string;
  noBookings: string;
  profileTitle: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  timezone: string;
  language: string;
  saveProfile: string;
  preferencesTitle: string;
  sms: string;
  whatsapp: string;
  chat: string;
  reminders: string;
  marketing: string;
  savePreferences: string;
  privacyDataTitle: string;
  privacyDataDescription: string;
  processing: string;
  requestDataCopy: string;
  requestDataErasure: string;
};

export type ProviderFeedbackCopy = {
  imageConversionUnsupported: string;
  imagePreparationFailed: string;
  imageConversionFailed: string;
  blockedSchedule: string;
  event: string;
  freeSlot: string;
  pendingBooking: string;
  customer: string;
  providerEventsLoadFailed: string;
  checkinAlreadyRegistered: string;
  checkinConfirmed: string;
  qrValidationFailed: string;
  cameraAccessFailed: string;
  portalLoadFailed: string;
  managementDisabled: string;
  invalidDateRange: string;
  urgentBlockApplied: string;
  requestSent: string;
  requestSubmitFailed: string;
  withdrawConfirm: string;
  requestWithdrawn: string;
  withdrawFailed: string;
  preferencesSaved: string;
  preferencesSaveFailed: string;
  profileUpdated: string;
  profileSaveFailed: string;
  photoUpdated: string;
  photoSaveFailed: string;
  notificationMarkedRead: string;
  notificationMarkReadFailed: string;
  sessionRevoked: string;
  sessionRevokeFailed: string;
  scheduleAccessDenied: string;
  exceptionWithdrawConfirm: string;
  exceptionWithdrawn: string;
  exceptionWithdrawFailed: string;
  scheduleDeleteConfirm: string;
  scheduleDeleteRequested: string;
  scheduleDeleteFailed: string;
  exceptionWithdrawReason: string;
  scheduleDeleteReason: string;
};

export type ProviderCopy = {
  greeting: (name: string) => string;
  status: (value: string) => string;
  participants: (count: number) => string;
  feedback: ProviderFeedbackCopy;
  text: Record<string, string>;
};

export type AuthCopy = {
  mfaDescription: (email: string) => string;
  passwordChangeDescription: (email: string) => string;
  passwordUpdated: (revokedSessions: number) => string;
  accountActivated: (email: string) => string;
  sessionStarted: (name: string) => string;
  passwordRequirements: string;
  newPasswordTooShort: string;
  newPasswordMismatch: string;
  invitationInvalid: string;
  mfaSetupFailed: string;
  mfaCodeInvalid: string;
  passwordUpdateFailed: string;
  mfaRequiredForPassword: string;
  recoveryRequested: string;
  recoveryRequestFailed: string;
  recoveryResetInvalid: string;
  loadingPortal: string;
  pendingProviderSetup: string;
  publicMode: string;
  apiUnavailable: string;
  portalLoadFailed: string;
  signInFailed: string;
  mfaVerificationFailed: string;
  mfaConfigured: string;
  passwordUpdatedShort: string;
  text: Record<string, string>;
};

const copies: Record<PortalLocale, PublicExperienceCopy> = {
  es: {
    account: "Mi cuenta",
    publicActions: "Acciones públicas",
    portraitAlt: (name) => `Retrato de ${name}`,
    viewProfile: (name) => `Ver el perfil público de ${name}`,
    recommended: "Recomendado",
    professional: "Profesional",
    serviceCount: (count) => `${count} servicio${count === 1 ? "" : "s"}`,
    noProviders: "No encontramos profesionales",
    filteredEmpty: "Probá quitar un filtro o buscar con otra palabra. También podés pedirle al Concierge que te ayude a encontrar una opción.",
    unpublishedEmpty: "Todavía no hay perfiles publicados para mostrar.",
    clearFilters: "Limpiar filtros",
    talkToConcierge: "Hablar con Concierge",
    catalogSeoTitle: (businessName) => `${businessName || "Kuroo"} · Elegí con quién reservar`,
    catalogSeoDescription: "Encontrá un profesional, revisá sus servicios y reservá tu próximo turno.",
    openAgenda: "Agenda abierta",
    heroTitle: "Elegí a la persona",
    heroTitleEmphasis: "con quien querés trabajar.",
    heroDescription: "Perfiles reales, servicios claros y horarios que podés reservar sin vueltas.",
    heroNote: "Reservas simples.",
    heroNoteEmphasis: "Tiempo bien cuidado.",
    directoryFilters: "Filtros del directorio",
    searchProvider: "Buscar profesional",
    filterByService: "Filtrar por servicio",
    filterByLocation: "Filtrar por sede",
    allServices: "Todos los servicios",
    allLocations: "Todas las sedes",
    directory: "Directorio",
    directoryHeading: "Personas para tu próxima cita",
    searching: "Buscando…",
    profileCount: (count) => `${count} perfil${count === 1 ? "" : "es"}`,
    localTimezone: "Horarios en el huso local",
    securePayment: "Pago seguro según la configuración",
    immediateConfirmation: "Confirmación inmediata",
    concierge: "Concierge",
    conciergeDescription: "Estoy acá para ayudarte a encontrar el servicio y horario ideal.",
    footer: "Con tecnología de Kuroo · Reservas con intención",
    profileError: "Este perfil no está disponible.",
    directoryLoadError: "No se pudo cargar el directorio público.",
    profileLoadError: "No se pudo cargar este perfil.",
    backToDirectory: "Volver al directorio",
    publicProfile: "Perfil público",
    profileDefaultBio: "Un espacio para trabajar con tiempo, claridad y atención.",
    scheduleIn: "Horarios en",
    chooseFirst: "Elegí primero",
    whatToBook: "¿Qué querés reservar?",
    selectServiceForSchedule: (name) => `Seleccioná un servicio para ver la agenda exacta de ${name}.`,
    serviceDuration: (minutes) => `${minutes} minutos`,
    activeWithoutServices: "Este perfil está activo, pero todavía no tiene servicios reservables.",
    modality: "Modalidad",
    whereToMeet: "¿Dónde nos encontramos?",
    availabilityInLocalTimezone: "La disponibilidad y el horario se muestran en el huso local del profesional.",
    chooseLocation: "Elegir sede",
    modalityConfirmedLater: "La modalidad se confirma al finalizar la reserva.",
    yourSchedule: "Tu agenda",
    chooseService: "Elegí un servicio",
    chooseServiceForSlots: "Elegí un servicio para consultar horarios.",
    needHelp: "¿Necesitás una mano?",
    conciergeKnowsProfile: "El Concierge conoce este perfil.",
    conciergeProfileDescription: "Podés preguntar por servicios, horarios o pedir que te acompañe durante la reserva."
  },
  en: {
    account: "My account",
    publicActions: "Public actions",
    portraitAlt: (name) => `Portrait of ${name}`,
    viewProfile: (name) => `View ${name}'s public profile`,
    recommended: "Recommended",
    professional: "Professional",
    serviceCount: (count) => `${count} service${count === 1 ? "" : "s"}`,
    noProviders: "We couldn't find any professionals",
    filteredEmpty: "Try removing a filter or searching for another word. You can also ask Concierge to help you find an option.",
    unpublishedEmpty: "There are no published profiles to show yet.",
    clearFilters: "Clear filters",
    talkToConcierge: "Talk to Concierge",
    catalogSeoTitle: (businessName) => `${businessName || "Kuroo"} · Choose who to book with`,
    catalogSeoDescription: "Find a professional, explore their services, and book your next appointment.",
    openAgenda: "Open availability",
    heroTitle: "Choose the person",
    heroTitleEmphasis: "you want to work with.",
    heroDescription: "Real profiles, clear services, and times you can book without the back and forth.",
    heroNote: "Simple booking.",
    heroNoteEmphasis: "Time well spent.",
    directoryFilters: "Directory filters",
    searchProvider: "Search professionals",
    filterByService: "Filter by service",
    filterByLocation: "Filter by location",
    allServices: "All services",
    allLocations: "All locations",
    directory: "Directory",
    directoryHeading: "People for your next appointment",
    searching: "Searching…",
    profileCount: (count) => `${count} profile${count === 1 ? "" : "s"}`,
    localTimezone: "Times in local time",
    securePayment: "Secure payment as configured",
    immediateConfirmation: "Immediate confirmation",
    concierge: "Concierge",
    conciergeDescription: "I'm here to help you find the right service and time.",
    footer: "Powered by Kuroo · Booking with intention",
    profileError: "This profile is not available.",
    directoryLoadError: "The public directory could not be loaded.",
    profileLoadError: "This profile could not be loaded.",
    backToDirectory: "Back to directory",
    publicProfile: "Public profile",
    profileDefaultBio: "A space to work with time, clarity, and care.",
    scheduleIn: "Times in",
    chooseFirst: "Choose first",
    whatToBook: "What would you like to book?",
    selectServiceForSchedule: (name) => `Select a service to see ${name}'s exact availability.`,
    serviceDuration: (minutes) => `${minutes} minutes`,
    activeWithoutServices: "This profile is active, but it has no bookable services yet.",
    modality: "Format",
    whereToMeet: "Where will we meet?",
    availabilityInLocalTimezone: "Availability and times are shown in the professional's local time zone.",
    chooseLocation: "Choose a location",
    modalityConfirmedLater: "The format is confirmed when you finish booking.",
    yourSchedule: "Your schedule",
    chooseService: "Choose a service",
    chooseServiceForSlots: "Choose a service to see available times.",
    needHelp: "Need a hand?",
    conciergeKnowsProfile: "Concierge knows this profile.",
    conciergeProfileDescription: "Ask about services or times, or let Concierge guide you through the booking."
  },
  pt: {
    account: "Minha conta",
    publicActions: "Ações públicas",
    portraitAlt: (name) => `Retrato de ${name}`,
    viewProfile: (name) => `Ver o perfil público de ${name}`,
    recommended: "Recomendado",
    professional: "Profissional",
    serviceCount: (count) => `${count} serviço${count === 1 ? "" : "s"}`,
    noProviders: "Não encontramos profissionais",
    filteredEmpty: "Tente remover um filtro ou buscar outra palavra. Você também pode pedir ajuda ao Concierge para encontrar uma opção.",
    unpublishedEmpty: "Ainda não há perfis publicados para exibir.",
    clearFilters: "Limpar filtros",
    talkToConcierge: "Falar com o Concierge",
    catalogSeoTitle: (businessName) => `${businessName || "Kuroo"} · Escolha com quem reservar`,
    catalogSeoDescription: "Encontre um profissional, confira os serviços e reserve seu próximo horário.",
    openAgenda: "Agenda aberta",
    heroTitle: "Escolha a pessoa",
    heroTitleEmphasis: "com quem você quer trabalhar.",
    heroDescription: "Perfis reais, serviços claros e horários que você pode reservar sem complicação.",
    heroNote: "Reservas simples.",
    heroNoteEmphasis: "Tempo bem cuidado.",
    directoryFilters: "Filtros do diretório",
    searchProvider: "Buscar profissional",
    filterByService: "Filtrar por serviço",
    filterByLocation: "Filtrar por unidade",
    allServices: "Todos os serviços",
    allLocations: "Todas as unidades",
    directory: "Diretório",
    directoryHeading: "Pessoas para seu próximo atendimento",
    searching: "Buscando…",
    profileCount: (count) => `${count} perfil${count === 1 ? "" : "s"}`,
    localTimezone: "Horários no fuso local",
    securePayment: "Pagamento seguro conforme a configuração",
    immediateConfirmation: "Confirmação imediata",
    concierge: "Concierge",
    conciergeDescription: "Estou aqui para ajudar você a encontrar o serviço e o horário ideal.",
    footer: "Tecnologia Kuroo · Reservas com intenção",
    profileError: "Este perfil não está disponível.",
    directoryLoadError: "Não foi possível carregar o diretório público.",
    profileLoadError: "Não foi possível carregar este perfil.",
    backToDirectory: "Voltar ao diretório",
    publicProfile: "Perfil público",
    profileDefaultBio: "Um espaço para trabalhar com tempo, clareza e atenção.",
    scheduleIn: "Horários em",
    chooseFirst: "Escolha primeiro",
    whatToBook: "O que você quer reservar?",
    selectServiceForSchedule: (name) => `Selecione um serviço para ver a agenda exata de ${name}.`,
    serviceDuration: (minutes) => `${minutes} minutos`,
    activeWithoutServices: "Este perfil está ativo, mas ainda não tem serviços disponíveis para reserva.",
    modality: "Modalidade",
    whereToMeet: "Onde vamos nos encontrar?",
    availabilityInLocalTimezone: "A disponibilidade e os horários aparecem no fuso local do profissional.",
    chooseLocation: "Escolher unidade",
    modalityConfirmedLater: "A modalidade é confirmada ao finalizar a reserva.",
    yourSchedule: "Sua agenda",
    chooseService: "Escolha um serviço",
    chooseServiceForSlots: "Escolha um serviço para consultar os horários.",
    needHelp: "Precisa de ajuda?",
    conciergeKnowsProfile: "O Concierge conhece este perfil.",
    conciergeProfileDescription: "Pergunte sobre serviços e horários, ou deixe o Concierge acompanhar sua reserva."
  }
};

const customerCopies: Record<PortalLocale, CustomerCopy> = {
  es: {
    loadingRecurring: "Cargando recurrencias…",
    recurringTitle: "Mis recurrencias",
    recurringDescription: "Consultá y gestioná cada ocurrencia sin acceder a datos de otros clientes.",
    recurringServiceFallback: "Servicio recurrente",
    recurringInterval: (value) => `cada ${value}`,
    recurringPayment: (value) => `pago ${value}`,
    recurringLoadFailed: "No se pudieron cargar las recurrencias.",
    cancelOccurrenceConfirm: "¿Cancelar esta ocurrencia?",
    cancelFutureConfirm: "¿Cancelar las ocurrencias futuras de esta serie?",
    occurrenceCancelled: "Ocurrencia cancelada.",
    futureCancelled: "Recurrencias futuras canceladas.",
    recurringCancelFailed: "No se pudo cancelar la recurrencia.",
    noSlotsForDate: "No hay horarios disponibles para esa fecha.",
    slotsLoadFailed: "No se pudieron consultar horarios.",
    selectSlot: "Elegí un horario disponible antes de aplicar el cambio.",
    occurrenceRescheduled: "Ocurrencia reprogramada.",
    rescheduleFailed: "No se pudo reprogramar la ocurrencia.",
    cancelFuture: "Cancelar futuras",
    meeting: "Videollamada",
    meetingAvailable: "Videollamada disponible",
    reschedule: "Reprogramar",
    cancel: "Cancelar",
    findAnotherDate: "Buscar otra fecha",
    searching: "Buscando…",
    searchTimes: "Buscar horarios",
    noAvailableTimes: "No hay horarios disponibles.",
    saving: "Guardando…",
    applyReschedule: "Aplicar reprogramación",
    status: (value) => ({ active: "Activa", paused: "Pausada", cancelled: "Cancelada", draft: "Borrador", held: "Retenida", booked: "Confirmada", rescheduled: "Reprogramada", unavailable: "No disponible", refunded: "Reembolsada", cancelled_occurrence: "Cancelada" }[value] ?? value),
    privacyTitle: "Solicitudes de datos",
    privacyDescription: "Consultá el estado y descargá tus exportaciones cuando estén listas.",
    refreshing: "Actualizando…",
    refresh: "Actualizar",
    dataCopy: "Copia de datos",
    dataErasure: "Borrado de datos",
    completed: "completada",
    download: "Descargar",
    noPrivacyRequests: "Todavía no hay solicitudes registradas.",
    privacyLoadFailed: "No se pudieron cargar las solicitudes de privacidad.",
    paymentsTitle: "Pagos y facturas",
    paymentsDescription: "Consultá sólo los movimientos asociados a tu cuenta.",
    payments: "Pagos",
    invoices: "Facturas",
    loading: "Cargando…",
    transactions: "Movimientos",
    noPayments: "Todavía no hay pagos registrados.",
    pending: "Pendiente",
    noInvoices: "Todavía no hay facturas emitidas.",
    paymentDetail: "Detalle del pago",
    close: "Cerrar",
    transactionCount: (count) => `${count} movimiento(s)`,
    noTransactions: "Sin movimientos registrados.",
    paymentDetailLoadFailed: "No se pudo cargar el detalle del pago.",
    workspaceGreeting: (name) => `Hola, ${name}`,
    workspaceDescription: "Consultá tus reservas y gestioná tus datos personales.",
    bookingFallback: "Reserva",
    workspaceLoadFailed: "No se pudo cargar tu espacio.",
    cancelBookingConfirm: "¿Cancelar esta reserva?",
    bookingCancelled: "Reserva cancelada.",
    bookingCancelFailed: "No se pudo cancelar la reserva.",
    profileUpdated: "Perfil actualizado.",
    profileUpdateFailed: "No se pudo actualizar el perfil.",
    preferencesSaved: "Preferencias guardadas.",
    preferencesSaveFailed: "No se pudieron guardar las preferencias.",
    privacyExportRequested: (id) => `Solicitud de copia recibida (${id}). Te avisaremos cuando esté lista.`,
    privacyExportFailed: "No se pudo solicitar la copia de tus datos.",
    privacyErasureConfirm: "¿Solicitar el borrado de tus datos? Esta acción inicia una revisión y puede cancelar reservas activas.",
    privacyErasureRequested: (id) => `Solicitud de borrado recibida (${id}). Te avisaremos cuando finalice la revisión.`,
    privacyErasureFailed: "No se pudo solicitar el borrado de tus datos.",
    signOut: "Cerrar sesión",
    bookingsTitle: "Mis reservas",
    bookingServiceFallback: "Servicio",
    noBookings: "Todavía no tenés reservas.",
    profileTitle: "Mi perfil",
    firstName: "Nombre",
    lastName: "Apellido",
    email: "Email",
    phone: "Teléfono",
    timezone: "Zona horaria",
    language: "Idioma",
    saveProfile: "Guardar perfil",
    preferencesTitle: "Preferencias",
    sms: "SMS",
    whatsapp: "WhatsApp",
    chat: "Chat",
    reminders: "Recordatorios",
    marketing: "Marketing",
    savePreferences: "Guardar preferencias",
    privacyDataTitle: "Privacidad y datos",
    privacyDataDescription: "Podés solicitar una copia de tus datos o iniciar el borrado de tu cuenta. Las solicitudes se procesan de forma segura y recibirás confirmación.",
    processing: "Procesando…",
    requestDataCopy: "Solicitar copia de mis datos",
    requestDataErasure: "Solicitar borrado de mis datos"
  },
  en: {
    loadingRecurring: "Loading recurring bookings…",
    recurringTitle: "My recurring bookings",
    recurringDescription: "Review and manage each occurrence without accessing other customers' data.",
    recurringServiceFallback: "Recurring service",
    recurringInterval: (value) => `every ${value}`,
    recurringPayment: (value) => `payment ${value}`,
    recurringLoadFailed: "Recurring bookings could not be loaded.",
    cancelOccurrenceConfirm: "Cancel this occurrence?",
    cancelFutureConfirm: "Cancel future occurrences of this series?",
    occurrenceCancelled: "Occurrence cancelled.",
    futureCancelled: "Future occurrences cancelled.",
    recurringCancelFailed: "The recurring booking could not be cancelled.",
    noSlotsForDate: "No times are available for that date.",
    slotsLoadFailed: "Available times could not be loaded.",
    selectSlot: "Choose an available time before applying the change.",
    occurrenceRescheduled: "Occurrence rescheduled.",
    rescheduleFailed: "The occurrence could not be rescheduled.",
    cancelFuture: "Cancel future bookings",
    meeting: "Video call",
    meetingAvailable: "Video call available",
    reschedule: "Reschedule",
    cancel: "Cancel",
    findAnotherDate: "Find another date",
    searching: "Searching…",
    searchTimes: "Find times",
    noAvailableTimes: "No times are available.",
    saving: "Saving…",
    applyReschedule: "Apply reschedule",
    status: (value) => ({ active: "Active", paused: "Paused", cancelled: "Cancelled", draft: "Draft", held: "Held", booked: "Confirmed", rescheduled: "Rescheduled", unavailable: "Unavailable", refunded: "Refunded", cancelled_occurrence: "Cancelled" }[value] ?? value),
    privacyTitle: "Data requests",
    privacyDescription: "Review the status of your exports and download them when they are ready.",
    refreshing: "Refreshing…",
    refresh: "Refresh",
    dataCopy: "Data copy",
    dataErasure: "Data erasure",
    completed: "completed",
    download: "Download",
    noPrivacyRequests: "There are no requests yet.",
    privacyLoadFailed: "Privacy requests could not be loaded.",
    paymentsTitle: "Payments and invoices",
    paymentsDescription: "Review only the transactions associated with your account.",
    payments: "Payments",
    invoices: "Invoices",
    loading: "Loading…",
    transactions: "Transactions",
    noPayments: "There are no payments yet.",
    pending: "Pending",
    noInvoices: "There are no invoices yet.",
    paymentDetail: "Payment details",
    close: "Close",
    transactionCount: (count) => `${count} transaction${count === 1 ? "" : "s"}`,
    noTransactions: "No transactions recorded.",
    paymentDetailLoadFailed: "Payment details could not be loaded.",
    workspaceGreeting: (name) => `Hello, ${name}`,
    workspaceDescription: "Review your bookings and manage your personal data.",
    bookingFallback: "Booking",
    workspaceLoadFailed: "Your workspace could not be loaded.",
    cancelBookingConfirm: "Cancel this booking?",
    bookingCancelled: "Booking cancelled.",
    bookingCancelFailed: "The booking could not be cancelled.",
    profileUpdated: "Profile updated.",
    profileUpdateFailed: "The profile could not be updated.",
    preferencesSaved: "Preferences saved.",
    preferencesSaveFailed: "Preferences could not be saved.",
    privacyExportRequested: (id) => `Data copy request received (${id}). We will let you know when it is ready.`,
    privacyExportFailed: "Your data copy could not be requested.",
    privacyErasureConfirm: "Request deletion of your data? This starts a review and may cancel active bookings.",
    privacyErasureRequested: (id) => `Deletion request received (${id}). We will let you know when the review is complete.`,
    privacyErasureFailed: "Data deletion could not be requested.",
    signOut: "Sign out",
    bookingsTitle: "My bookings",
    bookingServiceFallback: "Service",
    noBookings: "You do not have any bookings yet.",
    profileTitle: "My profile",
    firstName: "First name",
    lastName: "Last name",
    email: "Email",
    phone: "Phone",
    timezone: "Time zone",
    language: "Language",
    saveProfile: "Save profile",
    preferencesTitle: "Preferences",
    sms: "SMS",
    whatsapp: "WhatsApp",
    chat: "Chat",
    reminders: "Reminders",
    marketing: "Marketing",
    savePreferences: "Save preferences",
    privacyDataTitle: "Privacy and data",
    privacyDataDescription: "Request a copy of your data or start deleting your account. Requests are handled securely and you will receive confirmation.",
    processing: "Processing…",
    requestDataCopy: "Request a copy of my data",
    requestDataErasure: "Request deletion of my data"
  },
  pt: {
    loadingRecurring: "Carregando recorrências…",
    recurringTitle: "Minhas recorrências",
    recurringDescription: "Consulte e gerencie cada ocorrência sem acessar dados de outros clientes.",
    recurringServiceFallback: "Serviço recorrente",
    recurringInterval: (value) => `a cada ${value}`,
    recurringPayment: (value) => `pagamento ${value}`,
    recurringLoadFailed: "Não foi possível carregar as recorrências.",
    cancelOccurrenceConfirm: "Cancelar esta ocorrência?",
    cancelFutureConfirm: "Cancelar as ocorrências futuras desta série?",
    occurrenceCancelled: "Ocorrência cancelada.",
    futureCancelled: "Ocorrências futuras canceladas.",
    recurringCancelFailed: "Não foi possível cancelar a recorrência.",
    noSlotsForDate: "Não há horários disponíveis para essa data.",
    slotsLoadFailed: "Não foi possível consultar os horários.",
    selectSlot: "Escolha um horário disponível antes de aplicar a alteração.",
    occurrenceRescheduled: "Ocorrência reagendada.",
    rescheduleFailed: "Não foi possível reagendar a ocorrência.",
    cancelFuture: "Cancelar futuras",
    meeting: "Videochamada",
    meetingAvailable: "Videochamada disponível",
    reschedule: "Reagendar",
    cancel: "Cancelar",
    findAnotherDate: "Buscar outra data",
    searching: "Buscando…",
    searchTimes: "Buscar horários",
    noAvailableTimes: "Não há horários disponíveis.",
    saving: "Salvando…",
    applyReschedule: "Aplicar reagendamento",
    status: (value) => ({ active: "Ativa", paused: "Pausada", cancelled: "Cancelada", draft: "Rascunho", held: "Retida", booked: "Confirmada", rescheduled: "Reagendada", unavailable: "Indisponível", refunded: "Reembolsada", cancelled_occurrence: "Cancelada" }[value] ?? value),
    privacyTitle: "Solicitações de dados",
    privacyDescription: "Consulte o status e baixe suas exportações quando estiverem prontas.",
    refreshing: "Atualizando…",
    refresh: "Atualizar",
    dataCopy: "Cópia dos dados",
    dataErasure: "Exclusão de dados",
    completed: "concluída",
    download: "Baixar",
    noPrivacyRequests: "Ainda não há solicitações registradas.",
    privacyLoadFailed: "Não foi possível carregar as solicitações de privacidade.",
    paymentsTitle: "Pagamentos e faturas",
    paymentsDescription: "Consulte apenas os movimentos associados à sua conta.",
    payments: "Pagamentos",
    invoices: "Faturas",
    loading: "Carregando…",
    transactions: "Movimentos",
    noPayments: "Ainda não há pagamentos registrados.",
    pending: "Pendente",
    noInvoices: "Ainda não há faturas emitidas.",
    paymentDetail: "Detalhes do pagamento",
    close: "Fechar",
    transactionCount: (count) => `${count} movimentação${count === 1 ? "" : "ões"}`,
    noTransactions: "Nenhum movimento registrado.",
    paymentDetailLoadFailed: "Não foi possível carregar os detalhes do pagamento.",
    workspaceGreeting: (name) => `Olá, ${name}`,
    workspaceDescription: "Consulte suas reservas e gerencie seus dados pessoais.",
    bookingFallback: "Reserva",
    workspaceLoadFailed: "Não foi possível carregar seu espaço.",
    cancelBookingConfirm: "Cancelar esta reserva?",
    bookingCancelled: "Reserva cancelada.",
    bookingCancelFailed: "Não foi possível cancelar a reserva.",
    profileUpdated: "Perfil atualizado.",
    profileUpdateFailed: "Não foi possível atualizar o perfil.",
    preferencesSaved: "Preferências salvas.",
    preferencesSaveFailed: "Não foi possível salvar as preferências.",
    privacyExportRequested: (id) => `Solicitação de cópia recebida (${id}). Avisaremos quando estiver pronta.`,
    privacyExportFailed: "Não foi possível solicitar a cópia dos seus dados.",
    privacyErasureConfirm: "Solicitar a exclusão dos seus dados? Isso inicia uma revisão e pode cancelar reservas ativas.",
    privacyErasureRequested: (id) => `Solicitação de exclusão recebida (${id}). Avisaremos quando a revisão for concluída.`,
    privacyErasureFailed: "Não foi possível solicitar a exclusão dos seus dados.",
    signOut: "Sair",
    bookingsTitle: "Minhas reservas",
    bookingServiceFallback: "Serviço",
    noBookings: "Você ainda não tem reservas.",
    profileTitle: "Meu perfil",
    firstName: "Nome",
    lastName: "Sobrenome",
    email: "Email",
    phone: "Telefone",
    timezone: "Fuso horário",
    language: "Idioma",
    saveProfile: "Salvar perfil",
    preferencesTitle: "Preferências",
    sms: "SMS",
    whatsapp: "WhatsApp",
    chat: "Chat",
    reminders: "Lembretes",
    marketing: "Marketing",
    savePreferences: "Salvar preferências",
    privacyDataTitle: "Privacidade e dados",
    privacyDataDescription: "Solicite uma cópia dos seus dados ou inicie a exclusão da sua conta. As solicitações são processadas com segurança e você receberá uma confirmação.",
    processing: "Processando…",
    requestDataCopy: "Solicitar cópia dos meus dados",
    requestDataErasure: "Solicitar exclusão dos meus dados"
  }
};

const providerCopies: Record<PortalLocale, ProviderCopy> = {
  es: {
    greeting: (name) => `Hola, ${name}`,
    status: (value) => ({ pending: "Pendiente", approved: "Aprobada", rejected: "Rechazada", withdrawn: "Retirada", cancelled: "Cancelada" }[value] ?? value),
    participants: (count) => `${count} ${count === 1 ? "persona" : "personas"}`,
    feedback: {
      imageConversionUnsupported: "El navegador no puede convertir esta imagen. Elegí un archivo WebP.", imagePreparationFailed: "No se pudo preparar la imagen.", imageConversionFailed: "No se pudo convertir la imagen a WebP.", blockedSchedule: "Bloqueo de agenda", event: "Evento", freeSlot: "Hueco libre", pendingBooking: "Reserva pendiente", customer: "Cliente", providerEventsLoadFailed: "No se pudieron cargar tus eventos asignados.", checkinAlreadyRegistered: "El asistente ya estaba registrado.", checkinConfirmed: "Check-in confirmado correctamente.", qrValidationFailed: "No se pudo validar el QR.", cameraAccessFailed: "No se pudo acceder a la cámara. Podés ingresar el token manualmente.", portalLoadFailed: "No se pudo cargar el portal operativo.", managementDisabled: "Esta gestión está deshabilitada para tu rol.", invalidDateRange: "Indicá un inicio y fin válidos.", urgentBlockApplied: "Bloqueo urgente aplicado.", requestSent: "Solicitud enviada para aprobación.", requestSubmitFailed: "No se pudo enviar la solicitud.", withdrawConfirm: "¿Retirar esta solicitud?", requestWithdrawn: "Solicitud retirada.", withdrawFailed: "No se pudo retirar la solicitud.", preferencesSaved: "Preferencias guardadas.", preferencesSaveFailed: "No se pudieron guardar las preferencias.", profileUpdated: "Perfil actualizado.", profileSaveFailed: "No se pudo guardar el perfil.", photoUpdated: "Foto pública actualizada.", photoSaveFailed: "No se pudo guardar la foto.", notificationMarkedRead: "Aviso marcado como leído.", notificationMarkReadFailed: "No se pudo marcar el aviso como leído.", sessionRevoked: "Sesión revocada.", sessionRevokeFailed: "No se pudo revocar la sesión.", scheduleAccessDenied: "No hay gestiones de agenda habilitadas para tu rol.", exceptionWithdrawConfirm: "¿Solicitar el retiro de este bloqueo o ausencia?", exceptionWithdrawn: "Solicitud de retiro enviada para aprobación.", exceptionWithdrawFailed: "No se pudo solicitar el retiro.", scheduleDeleteConfirm: "¿Solicitar la eliminación de este horario?", scheduleDeleteRequested: "Solicitud de eliminación enviada para aprobación.", scheduleDeleteFailed: "No se pudo solicitar la eliminación.", exceptionWithdrawReason: "Retiro solicitado desde el portal", scheduleDeleteReason: "Eliminación solicitada desde el portal"
    },
    text: {
      portalOperational: "Portal operativo", portalDescription: "Tu agenda, tus solicitudes y tus avisos en un solo lugar.", publicPage: "Mi página pública", profile: "Mi perfil", signOut: "Cerrar sesión",
      todayAppointments: "Citas de hoy", includesPending: "Incluye reservas pendientes", pendingRequests: "Solicitudes pendientes", adminApproval: "Con aprobación administrativa", unreadAlerts: "Avisos sin leer", notificationCenter: "Centro de notificaciones", conflictsToReview: "Conflictos a revisar", bookingsDoNotCancel: "Las citas existentes no se cancelan solas",
      personalProfile: "Perfil personal", publicPhoto: "Foto pública", firstName: "Nombre", lastName: "Apellido", publicName: "Nombre público", email: "Email", phone: "Teléfono", timezone: "Zona horaria", language: "Idioma", saveProfile: "Guardar perfil", changePublicPhoto: "Cambiar foto pública", photoHelp: "JPG y PNG se convierten a WebP automáticamente.", notifications: "Notificaciones", notificationsRequired: "Email y portal son obligatorios para operación", operationalEmail: "Email operativo", reminders: "Recordatorios", sms: "SMS", whatsapp: "WhatsApp", news: "Novedades", required: "obligatorio", savePreferences: "Guardar preferencias", activeSessions: "Sesiones activas", lastActivity: "Última actividad", browser: "Navegador", currentSession: "Esta sesión", revoke: "Revocar",
      myCalendar: "Mi agenda", printPdf: "Imprimir / PDF", exportIcs: "Exportar ICS", from: "Desde", to: "Hasta", usualRange: "Rango habitual", showFreeSlots: "Mostrar huecos libres", max31Days: "Acotá la lista a 31 días para generar huecos.", today: "Hoy", selectedDate: "Fecha seleccionada", day: "Día", week: "Semana", month: "Mes", list: "Lista", location: "Sede", allLocations: "Todas mis sedes", service: "Servicio", allServices: "Todos mis servicios", status: "Estado", allStatuses: "Todos los estados", activePending: "Activas y pendientes", confirmed: "Confirmadas", pending: "Pendientes", cancelled: "Canceladas", rejected: "Rechazadas", noShow: "No se presentó", loadingCalendar: "Cargando agenda…",
      configuredHours: "Horarios configurados", scheduleNote: "Por servicio y sede; los cambios futuros requieren aprobación.", requestSchedule: "Solicitar horario", noConfiguredSlots: "Todavía no hay franjas configuradas.", edit: "Editar", copy: "Copiar", delete: "Eliminar",
      availabilityRequests: "Solicitudes de agenda", newRequest: "Nueva solicitud", type: "Tipo", absence: "Ausencia / bloqueo", opening: "Apertura puntual", scheduleChange: "Cambio de horario", effectiveDate: "Fecha efectiva", start: "Inicio", end: "Fin", urgent: "Aplicar como bloqueo urgente inmediato", urgentBlock: "Bloqueo urgente", allMyServices: "Todos mis servicios", allMySchedule: "Toda mi agenda", category: "Categoría", categoryPlaceholder: "Vacaciones, formación…", privateReason: "Motivo privado", privateReasonPlaceholder: "Sólo lo verán administración y responsables.", sending: "Enviando…", requestModification: "Solicitar modificación", sendRequest: "Enviar solicitud", noRequests: "Todavía no hay solicitudes.", marking: "Marcando…", markRead: "Marcar leída", noAlerts: "No hay avisos.",
      approvedBlocks: "Bloqueos y ausencias aprobados", exceptionNote: "Los cambios o retiros quedan activos hasta que administración los apruebe.", blocking: "Bloqueo / ausencia", noReason: "Sin motivo indicado", pendingRequest: "Solicitud pendiente", approved: "Aprobado", modify: "Modificar", withdraw: "Retirar", noApproved: "No hay bloqueos ni ausencias aprobados.",
      checkinTitle: "Check-in de eventos", checkinDescription: "Sólo podés validar entradas de eventos asignados a tu perfil.", noContact: "Sin datos de contacto", eventPeriod: "Evento y período", selectPeriod: "Seleccioná un período", noEvents: "No hay eventos asignados actualmente.", scannerAria: "Vista previa del escáner QR", scannerOverlay: "Apuntá al QR del asistente…", stopCamera: "Detener cámara", openCamera: "Abrir cámara", qrTokenAria: "Token QR manual", qrTokenPlaceholder: "Token QR manual", validating: "Validando…", validate: "Validar", unsupported: "Este navegador no soporta lectura QR automática; utilizá el token manual.", denied: "La cámara está bloqueada. Revisá los permisos del navegador o usá el token manual.", alreadyRegistered: "Ya registrado", checkinConfirmed: "Check-in confirmado", selectedPeriod: "Período seleccionado", calendarNoMovement: "Sin movimientos en este día.", videoCall: "Videollamada", available: "Libre", recurring: "Recurrente", participants: "persona", manualReview: "cita(s) requieren revisión manual.", response: "Respuesta"
    }
  },
  en: {
    greeting: (name) => `Hello, ${name}`,
    status: (value) => ({ pending: "Pending", approved: "Approved", rejected: "Rejected", withdrawn: "Withdrawn", cancelled: "Cancelled" }[value] ?? value),
    participants: (count) => `${count} ${count === 1 ? "person" : "people"}`,
    feedback: {
      imageConversionUnsupported: "This browser cannot convert the image. Choose a WebP file.", imagePreparationFailed: "The image could not be prepared.", imageConversionFailed: "The image could not be converted to WebP.", blockedSchedule: "Schedule block", event: "Event", freeSlot: "Available slot", pendingBooking: "Pending booking", customer: "Customer", providerEventsLoadFailed: "Assigned events could not be loaded.", checkinAlreadyRegistered: "The attendee was already registered.", checkinConfirmed: "Check-in confirmed.", qrValidationFailed: "The QR code could not be validated.", cameraAccessFailed: "Camera access failed. You can enter the manual token instead.", portalLoadFailed: "The operations portal could not be loaded.", managementDisabled: "This action is disabled for your role.", invalidDateRange: "Enter a valid start and end.", urgentBlockApplied: "Urgent block applied.", requestSent: "Request sent for approval.", requestSubmitFailed: "The request could not be sent.", withdrawConfirm: "Withdraw this request?", requestWithdrawn: "Request withdrawn.", withdrawFailed: "The request could not be withdrawn.", preferencesSaved: "Preferences saved.", preferencesSaveFailed: "Preferences could not be saved.", profileUpdated: "Profile updated.", profileSaveFailed: "The profile could not be saved.", photoUpdated: "Public photo updated.", photoSaveFailed: "The photo could not be saved.", notificationMarkedRead: "Notification marked as read.", notificationMarkReadFailed: "The notification could not be marked as read.", sessionRevoked: "Session revoked.", sessionRevokeFailed: "The session could not be revoked.", scheduleAccessDenied: "No schedule management actions are enabled for your role.", exceptionWithdrawConfirm: "Request removal of this block or time off?", exceptionWithdrawn: "Removal request sent for approval.", exceptionWithdrawFailed: "The removal request could not be sent.", scheduleDeleteConfirm: "Request deletion of this schedule?", scheduleDeleteRequested: "Deletion request sent for approval.", scheduleDeleteFailed: "The deletion request could not be sent.", exceptionWithdrawReason: "Removal requested from the portal", scheduleDeleteReason: "Deletion requested from the portal"
    },
    text: {
      portalOperational: "Operations portal", portalDescription: "Your calendar, requests, and alerts in one place.", publicPage: "My public page", profile: "My profile", signOut: "Sign out",
      todayAppointments: "Today's appointments", includesPending: "Includes pending bookings", pendingRequests: "Pending requests", adminApproval: "Requires administrative approval", unreadAlerts: "Unread alerts", notificationCenter: "Notification center", conflictsToReview: "Conflicts to review", bookingsDoNotCancel: "Existing appointments are not cancelled automatically",
      personalProfile: "Personal profile", publicPhoto: "Public photo", firstName: "First name", lastName: "Last name", publicName: "Public name", email: "Email", phone: "Phone", timezone: "Time zone", language: "Language", saveProfile: "Save profile", changePublicPhoto: "Change public photo", photoHelp: "JPG and PNG files are converted to WebP automatically.", notifications: "Notifications", notificationsRequired: "Email and portal are required for operations", operationalEmail: "Operational email", reminders: "Reminders", sms: "SMS", whatsapp: "WhatsApp", news: "Updates", required: "required", savePreferences: "Save preferences", activeSessions: "Active sessions", lastActivity: "Last activity", browser: "Browser", currentSession: "This session", revoke: "Revoke",
      myCalendar: "My calendar", printPdf: "Print / PDF", exportIcs: "Export ICS", from: "From", to: "To", usualRange: "Usual range", showFreeSlots: "Show free slots", max31Days: "Limit the list to 31 days to generate free slots.", today: "Today", selectedDate: "Selected date", day: "Day", week: "Week", month: "Month", list: "List", location: "Location", allLocations: "All my locations", service: "Service", allServices: "All my services", status: "Status", allStatuses: "All statuses", activePending: "Active and pending", confirmed: "Confirmed", pending: "Pending", cancelled: "Cancelled", rejected: "Rejected", noShow: "No-show", loadingCalendar: "Loading calendar…",
      configuredHours: "Configured hours", scheduleNote: "By service and location; future changes require approval.", requestSchedule: "Request schedule", noConfiguredSlots: "No time ranges configured yet.", edit: "Edit", copy: "Copy", delete: "Delete",
      availabilityRequests: "Schedule requests", newRequest: "New request", type: "Type", absence: "Time off / block", opening: "One-time opening", scheduleChange: "Schedule change", effectiveDate: "Effective date", start: "Start", end: "End", urgent: "Apply as an immediate urgent block", urgentBlock: "Urgent block", allMyServices: "All my services", allMySchedule: "My entire schedule", category: "Category", categoryPlaceholder: "Vacation, training…", privateReason: "Private reason", privateReasonPlaceholder: "Only administrators and managers will see this.", sending: "Sending…", requestModification: "Request change", sendRequest: "Send request", noRequests: "There are no requests yet.", marking: "Marking…", markRead: "Mark as read", noAlerts: "No alerts.",
      approvedBlocks: "Approved blocks and time off", exceptionNote: "Changes or withdrawals remain active until administration approves them.", blocking: "Block / time off", noReason: "No reason provided", pendingRequest: "Pending request", approved: "Approved", modify: "Modify", withdraw: "Withdraw", noApproved: "No approved blocks or time off.",
      checkinTitle: "Event check-in", checkinDescription: "You can only validate entry for events assigned to your profile.", noContact: "No contact details", eventPeriod: "Event and period", selectPeriod: "Select a period", noEvents: "No events are currently assigned.", scannerAria: "QR scanner preview", scannerOverlay: "Point at the attendee's QR code…", stopCamera: "Stop camera", openCamera: "Open camera", qrTokenAria: "Manual QR token", qrTokenPlaceholder: "Manual QR token", validating: "Validating…", validate: "Validate", unsupported: "This browser does not support automatic QR scanning; use the manual token.", denied: "Camera access is blocked. Check browser permissions or use the manual token.", alreadyRegistered: "Already registered", checkinConfirmed: "Check-in confirmed", selectedPeriod: "Selected period", calendarNoMovement: "No activity on this day.", videoCall: "Video call", available: "Available", recurring: "Recurring", participants: "people", manualReview: "appointment(s) require manual review.", response: "Response"
    }
  },
  pt: {
    greeting: (name) => `Olá, ${name}`,
    status: (value) => ({ pending: "Pendente", approved: "Aprovada", rejected: "Rejeitada", withdrawn: "Retirada", cancelled: "Cancelada" }[value] ?? value),
    participants: (count) => `${count} ${count === 1 ? "pessoa" : "pessoas"}`,
    feedback: {
      imageConversionUnsupported: "Este navegador não consegue converter a imagem. Escolha um arquivo WebP.", imagePreparationFailed: "Não foi possível preparar a imagem.", imageConversionFailed: "Não foi possível converter a imagem para WebP.", blockedSchedule: "Bloqueio de agenda", event: "Evento", freeSlot: "Horário livre", pendingBooking: "Reserva pendente", customer: "Cliente", providerEventsLoadFailed: "Não foi possível carregar os eventos atribuídos.", checkinAlreadyRegistered: "O participante já estava registrado.", checkinConfirmed: "Check-in confirmado.", qrValidationFailed: "Não foi possível validar o QR.", cameraAccessFailed: "Não foi possível acessar a câmera. Você pode informar o token manual.", portalLoadFailed: "Não foi possível carregar o portal operacional.", managementDisabled: "Esta ação está desabilitada para o seu perfil.", invalidDateRange: "Informe um início e fim válidos.", urgentBlockApplied: "Bloqueio urgente aplicado.", requestSent: "Solicitação enviada para aprovação.", requestSubmitFailed: "Não foi possível enviar a solicitação.", withdrawConfirm: "Retirar esta solicitação?", requestWithdrawn: "Solicitação retirada.", withdrawFailed: "Não foi possível retirar a solicitação.", preferencesSaved: "Preferências salvas.", preferencesSaveFailed: "Não foi possível salvar as preferências.", profileUpdated: "Perfil atualizado.", profileSaveFailed: "Não foi possível salvar o perfil.", photoUpdated: "Foto pública atualizada.", photoSaveFailed: "Não foi possível salvar a foto.", notificationMarkedRead: "Aviso marcado como lido.", notificationMarkReadFailed: "Não foi possível marcar o aviso como lido.", sessionRevoked: "Sessão revogada.", sessionRevokeFailed: "Não foi possível revogar a sessão.", scheduleAccessDenied: "Não há ações de agenda habilitadas para o seu perfil.", exceptionWithdrawConfirm: "Solicitar a retirada deste bloqueio ou ausência?", exceptionWithdrawn: "Solicitação de retirada enviada para aprovação.", exceptionWithdrawFailed: "Não foi possível solicitar a retirada.", scheduleDeleteConfirm: "Solicitar a exclusão deste horário?", scheduleDeleteRequested: "Solicitação de exclusão enviada para aprovação.", scheduleDeleteFailed: "Não foi possível solicitar a exclusão.", exceptionWithdrawReason: "Retirada solicitada pelo portal", scheduleDeleteReason: "Exclusão solicitada pelo portal"
    },
    text: {
      portalOperational: "Portal operacional", portalDescription: "Sua agenda, suas solicitações e seus avisos em um só lugar.", publicPage: "Minha página pública", profile: "Meu perfil", signOut: "Sair",
      todayAppointments: "Atendimentos de hoje", includesPending: "Inclui reservas pendentes", pendingRequests: "Solicitações pendentes", adminApproval: "Com aprovação administrativa", unreadAlerts: "Avisos não lidos", notificationCenter: "Central de notificações", conflictsToReview: "Conflitos para revisar", bookingsDoNotCancel: "Os atendimentos existentes não são cancelados automaticamente",
      personalProfile: "Perfil pessoal", publicPhoto: "Foto pública", firstName: "Nome", lastName: "Sobrenome", publicName: "Nome público", email: "Email", phone: "Telefone", timezone: "Fuso horário", language: "Idioma", saveProfile: "Salvar perfil", changePublicPhoto: "Alterar foto pública", photoHelp: "Arquivos JPG e PNG são convertidos para WebP automaticamente.", notifications: "Notificações", notificationsRequired: "Email e portal são obrigatórios para a operação", operationalEmail: "Email operacional", reminders: "Lembretes", sms: "SMS", whatsapp: "WhatsApp", news: "Novidades", required: "obrigatório", savePreferences: "Salvar preferências", activeSessions: "Sessões ativas", lastActivity: "Última atividade", browser: "Navegador", currentSession: "Esta sessão", revoke: "Revogar",
      myCalendar: "Minha agenda", printPdf: "Imprimir / PDF", exportIcs: "Exportar ICS", from: "De", to: "Até", usualRange: "Intervalo habitual", showFreeSlots: "Mostrar horários livres", max31Days: "Limite a lista a 31 dias para gerar horários livres.", today: "Hoje", selectedDate: "Data selecionada", day: "Dia", week: "Semana", month: "Mês", list: "Lista", location: "Unidade", allLocations: "Todas as minhas unidades", service: "Serviço", allServices: "Todos os meus serviços", status: "Status", allStatuses: "Todos os status", activePending: "Ativos e pendentes", confirmed: "Confirmadas", pending: "Pendentes", cancelled: "Canceladas", rejected: "Rejeitadas", noShow: "Não compareceu", loadingCalendar: "Carregando agenda…",
      configuredHours: "Horários configurados", scheduleNote: "Por serviço e unidade; alterações futuras exigem aprovação.", requestSchedule: "Solicitar horário", noConfiguredSlots: "Ainda não há faixas configuradas.", edit: "Editar", copy: "Copiar", delete: "Excluir",
      availabilityRequests: "Solicitações de agenda", newRequest: "Nova solicitação", type: "Tipo", absence: "Ausência / bloqueio", opening: "Abertura pontual", scheduleChange: "Alteração de horário", effectiveDate: "Data efetiva", start: "Início", end: "Fim", urgent: "Aplicar como bloqueio urgente imediato", urgentBlock: "Bloqueio urgente", allMyServices: "Todos os meus serviços", allMySchedule: "Toda a minha agenda", category: "Categoria", categoryPlaceholder: "Férias, formação…", privateReason: "Motivo privado", privateReasonPlaceholder: "Somente administração e responsáveis verão isso.", sending: "Enviando…", requestModification: "Solicitar alteração", sendRequest: "Enviar solicitação", noRequests: "Ainda não há solicitações.", marking: "Marcando…", markRead: "Marcar como lida", noAlerts: "Não há avisos.",
      approvedBlocks: "Bloqueios e ausências aprovados", exceptionNote: "Alterações ou retiradas permanecem ativas até serem aprovadas pela administração.", blocking: "Bloqueio / ausência", noReason: "Nenhum motivo informado", pendingRequest: "Solicitação pendente", approved: "Aprovado", modify: "Modificar", withdraw: "Retirar", noApproved: "Não há bloqueios ou ausências aprovados.",
      checkinTitle: "Check-in de eventos", checkinDescription: "Você só pode validar entradas de eventos atribuídos ao seu perfil.", noContact: "Sem dados de contato", eventPeriod: "Evento e período", selectPeriod: "Selecione um período", noEvents: "Não há eventos atribuídos atualmente.", scannerAria: "Prévia do leitor QR", scannerOverlay: "Aponte para o QR do participante…", stopCamera: "Parar câmera", openCamera: "Abrir câmera", qrTokenAria: "Token QR manual", qrTokenPlaceholder: "Token QR manual", validating: "Validando…", validate: "Validar", unsupported: "Este navegador não suporta leitura automática de QR; use o token manual.", denied: "A câmera está bloqueada. Verifique as permissões do navegador ou use o token manual.", alreadyRegistered: "Já registrado", checkinConfirmed: "Check-in confirmado", selectedPeriod: "Período selecionado", calendarNoMovement: "Sem movimentações neste dia.", videoCall: "Videochamada", available: "Livre", recurring: "Recorrente", participants: "pessoas", manualReview: "atendimento(s) exigem revisão manual.", response: "Resposta"
    }
  }
};

const authCopies: Record<PortalLocale, AuthCopy> = {
  es: {
    mfaDescription: (email) => `La cuenta de ${email} requiere un segundo factor. Escaneá el QR de tu autenticador o cargá esta clave manualmente.`,
    passwordChangeDescription: (email) => `La cuenta de ${email} fue creada con una contraseña temporal. Elegí una contraseña personal para continuar.`,
    passwordUpdated: (revokedSessions) => `Contraseña actualizada. Se revocaron ${revokedSessions} sesiones anteriores.`,
    accountActivated: (email) => `Cuenta activada para ${email}`,
    sessionStarted: (name) => `Sesión de ${name}`,
    passwordRequirements: "La contraseña debe tener al menos 12 caracteres y coincidir con su confirmación.",
    newPasswordTooShort: "La nueva contraseña debe tener al menos 12 caracteres.",
    newPasswordMismatch: "Las contraseñas nuevas no coinciden.",
    invitationInvalid: "La invitación no es válida o ya expiró.",
    mfaSetupFailed: "No se pudo iniciar la configuración MFA.",
    mfaCodeInvalid: "El código MFA no es válido.",
    passwordUpdateFailed: "No se pudo actualizar la contraseña.",
    mfaRequiredForPassword: "Completá el segundo factor antes de cambiar la contraseña.",
    recoveryRequested: "Si la cuenta existe, recibirás un enlace de recuperación por email.",
    recoveryRequestFailed: "No se pudo solicitar la recuperación.",
    recoveryResetInvalid: "El enlace de recuperación no es válido o ya expiró.",
    loadingPortal: "Cargando portal…",
    pendingProviderSetup: "Contraseña guardada. Un administrador debe completar servicios, sedes y horario antes de activar tu cuenta.",
    publicMode: "Modo público",
    apiUnavailable: "La API no está disponible.",
    portalLoadFailed: "No se pudo cargar el portal",
    signInFailed: "No se pudo iniciar sesión.",
    mfaVerificationFailed: "No se pudo verificar el segundo factor.",
    mfaConfigured: "MFA configurado correctamente",
    passwordUpdatedShort: "Contraseña actualizada",
    text: {
      invitationTitle: "Aceptá tu invitación", invitationDescription: "Definí una contraseña segura para activar tu cuenta. El enlace es de un solo uso y expira automáticamente.", newPassword: "Nueva contraseña", minimumPassword: "Mínimo 12 caracteres.", passwordDoNotReuse: "No la reutilices.", confirmPassword: "Confirmar contraseña", activating: "Activando…", activateAccount: "Activar cuenta", validatingInvitation: "Validando invitación…", neverShare: "Nunca compartas este enlace ni tu contraseña.", invitationAuthenticatorLabel: "Authenticator de invitación", notAvailable: "No disponible",
      mfaTitle: "Configurá MFA para continuar", retry: "Reintentar", generating: "Generando tu configuración segura…", openAuthenticator: "Abrir en tu autenticador", manualKey: "Clave manual", sixDigitCode: "Código de 6 dígitos", verifying: "Verificando…", activateMfa: "Activar MFA y continuar", recoveryCodes: "Guardá tus códigos de recuperación", shownOnce: "Estos códigos se muestran una sola vez.", signOut: "Cerrar sesión",
      passwordChangeTitle: "Actualizá tu contraseña", temporaryPassword: "Contraseña temporal", choosePersonalPassword: "La cuenta fue creada con una contraseña temporal. Elegí una contraseña personal para continuar.", saveContinue: "Guardar y continuar",
      chooseNewPassword: "Elegí una nueva contraseña", recoverPassword: "Recuperá tu contraseña", recoveryTokenDescription: "El enlace es válido durante 30 minutos y sólo puede utilizarse una vez.", recoveryRequestDescription: "Indicá el email de acceso de tu portal operativo. La respuesta será la misma aunque no exista una cuenta.", email: "Email", savePassword: "Guardar contraseña", saving: "Guardando…", sendLink: "Enviar enlace", sending: "Enviando…", backToAccess: "Volver al acceso",
      extraVerification: "Verificación adicional", completeSecondFactor: "Completá el segundo factor para continuar.", recoveryCode: "Código de recuperación", authenticationCode: "Código de autenticación", verify: "Verificar", useAuthenticator: "Usar autenticador", useRecoveryCode: "Usar código de recuperación", access: "Acceso", accessDescription: "Ingresá para consultar tu portal.", password: "Contraseña", signingIn: "Ingresando…", signIn: "Ingresar", forgotPassword: "¿Olvidaste tu contraseña?", publicBookingTitle: "Reservá tu próxima cita", portalDescription: "Portal de cliente y acceso a reservas embebibles.", services: "Servicios", noServices: "No hay servicios disponibles.", schedule: "Agendar", widgetDescription: "El widget soporta retención temporal y confirmación segura.", openingActivation: "Abriendo la activación en Backoffice…", openingOperations: "Abriendo tu espacio operativo en Backoffice…"
    }
  },
  en: {
    mfaDescription: (email) => `The account ${email} requires a second factor. Scan the QR code in your authenticator or enter this key manually.`,
    passwordChangeDescription: (email) => `The account ${email} was created with a temporary password. Choose a personal password to continue.`,
    passwordUpdated: (revokedSessions) => `Password updated. ${revokedSessions} previous session${revokedSessions === 1 ? "" : "s"} revoked.`,
    accountActivated: (email) => `Account activated for ${email}`,
    sessionStarted: (name) => `Session for ${name}`,
    passwordRequirements: "The password must be at least 12 characters long and match its confirmation.",
    newPasswordTooShort: "The new password must be at least 12 characters long.",
    newPasswordMismatch: "The new passwords do not match.",
    invitationInvalid: "The invitation is invalid or has expired.",
    mfaSetupFailed: "MFA setup could not be started.",
    mfaCodeInvalid: "The MFA code is invalid.",
    passwordUpdateFailed: "The password could not be updated.",
    mfaRequiredForPassword: "Complete the second factor before changing the password.",
    recoveryRequested: "If the account exists, you will receive a recovery email.",
    recoveryRequestFailed: "The recovery request could not be submitted.",
    recoveryResetInvalid: "The recovery link is invalid or has expired.",
    loadingPortal: "Loading portal…",
    pendingProviderSetup: "Password saved. An administrator must finish setting up services, locations, and hours before activating your account.",
    publicMode: "Public mode",
    apiUnavailable: "The API is not available.",
    portalLoadFailed: "The portal could not be loaded",
    signInFailed: "Sign-in failed.",
    mfaVerificationFailed: "The second factor could not be verified.",
    mfaConfigured: "MFA configured successfully",
    passwordUpdatedShort: "Password updated",
    text: {
      invitationTitle: "Accept your invitation", invitationDescription: "Set a secure password to activate your account. This one-time link expires automatically.", newPassword: "New password", minimumPassword: "At least 12 characters.", passwordDoNotReuse: "Do not reuse it.", confirmPassword: "Confirm password", activating: "Activating…", activateAccount: "Activate account", validatingInvitation: "Validating invitation…", neverShare: "Never share this link or your password.", invitationAuthenticatorLabel: "Invitation authenticator", notAvailable: "Not available",
      mfaTitle: "Set up MFA to continue", retry: "Retry", generating: "Generating your secure setup…", openAuthenticator: "Open in your authenticator", manualKey: "Manual key", sixDigitCode: "6-digit code", verifying: "Verifying…", activateMfa: "Activate MFA and continue", recoveryCodes: "Save your recovery codes", shownOnce: "These codes are shown only once.", signOut: "Sign out",
      passwordChangeTitle: "Update your password", temporaryPassword: "Temporary password", choosePersonalPassword: "Your account was created with a temporary password. Choose a personal password to continue.", saveContinue: "Save and continue",
      chooseNewPassword: "Choose a new password", recoverPassword: "Recover your password", recoveryTokenDescription: "This link is valid for 30 minutes and can only be used once.", recoveryRequestDescription: "Enter the email you use for your operations portal. The response is the same even when no account exists.", email: "Email", savePassword: "Save password", saving: "Saving…", sendLink: "Send link", sending: "Sending…", backToAccess: "Back to sign in",
      extraVerification: "Additional verification", completeSecondFactor: "Complete the second factor to continue.", recoveryCode: "Recovery code", authenticationCode: "Authentication code", verify: "Verify", useAuthenticator: "Use authenticator", useRecoveryCode: "Use recovery code", access: "Sign in", accessDescription: "Sign in to view your portal.", password: "Password", signingIn: "Signing in…", signIn: "Sign in", forgotPassword: "Forgot your password?", publicBookingTitle: "Book your next appointment", portalDescription: "Customer portal and embeddable booking access.", services: "Services", noServices: "No services are available.", schedule: "Book an appointment", widgetDescription: "The widget supports temporary holds and secure confirmation.", openingActivation: "Opening activation in Backoffice…", openingOperations: "Opening your operations space in Backoffice…"
    }
  },
  pt: {
    mfaDescription: (email) => `A conta ${email} exige um segundo fator. Escaneie o QR no seu autenticador ou informe esta chave manualmente.`,
    passwordChangeDescription: (email) => `A conta ${email} foi criada com uma senha temporária. Escolha uma senha pessoal para continuar.`,
    passwordUpdated: (revokedSessions) => `Senha atualizada. ${revokedSessions} sessão${revokedSessions === 1 ? "" : "ões"} anterior${revokedSessions === 1 ? "" : "es"} revogada${revokedSessions === 1 ? "" : "s"}.`,
    accountActivated: (email) => `Conta ativada para ${email}`,
    sessionStarted: (name) => `Sessão de ${name}`,
    passwordRequirements: "A senha deve ter pelo menos 12 caracteres e coincidir com a confirmação.",
    newPasswordTooShort: "A nova senha deve ter pelo menos 12 caracteres.",
    newPasswordMismatch: "As novas senhas não coincidem.",
    invitationInvalid: "O convite é inválido ou expirou.",
    mfaSetupFailed: "Não foi possível iniciar a configuração do MFA.",
    mfaCodeInvalid: "O código MFA é inválido.",
    passwordUpdateFailed: "Não foi possível atualizar a senha.",
    mfaRequiredForPassword: "Conclua o segundo fator antes de alterar a senha.",
    recoveryRequested: "Se a conta existir, você receberá um email de recuperação.",
    recoveryRequestFailed: "Não foi possível solicitar a recuperação.",
    recoveryResetInvalid: "O link de recuperação é inválido ou expirou.",
    loadingPortal: "Carregando portal…",
    pendingProviderSetup: "Senha salva. Um administrador precisa concluir a configuração de serviços, unidades e horários antes de ativar sua conta.",
    publicMode: "Modo público",
    apiUnavailable: "A API não está disponível.",
    portalLoadFailed: "Não foi possível carregar o portal",
    signInFailed: "Não foi possível entrar.",
    mfaVerificationFailed: "Não foi possível verificar o segundo fator.",
    mfaConfigured: "MFA configurado com sucesso",
    passwordUpdatedShort: "Senha atualizada",
    text: {
      invitationTitle: "Aceite seu convite", invitationDescription: "Defina uma senha segura para ativar sua conta. O link é de uso único e expira automaticamente.", newPassword: "Nova senha", minimumPassword: "Mínimo de 12 caracteres.", passwordDoNotReuse: "Não reutilize a senha.", confirmPassword: "Confirmar senha", activating: "Ativando…", activateAccount: "Ativar conta", validatingInvitation: "Validando convite…", neverShare: "Nunca compartilhe este link ou sua senha.", invitationAuthenticatorLabel: "Autenticador do convite", notAvailable: "Indisponível",
      mfaTitle: "Configure o MFA para continuar", retry: "Tentar novamente", generating: "Gerando sua configuração segura…", openAuthenticator: "Abrir no autenticador", manualKey: "Chave manual", sixDigitCode: "Código de 6 dígitos", verifying: "Verificando…", activateMfa: "Ativar MFA e continuar", recoveryCodes: "Guarde seus códigos de recuperação", shownOnce: "Estes códigos são exibidos apenas uma vez.", signOut: "Sair",
      passwordChangeTitle: "Atualize sua senha", temporaryPassword: "Senha temporária", choosePersonalPassword: "Sua conta foi criada com uma senha temporária. Escolha uma senha pessoal para continuar.", saveContinue: "Salvar e continuar",
      chooseNewPassword: "Escolha uma nova senha", recoverPassword: "Recupere sua senha", recoveryTokenDescription: "O link é válido por 30 minutos e só pode ser usado uma vez.", recoveryRequestDescription: "Informe o email de acesso ao seu portal operacional. A resposta será a mesma mesmo que a conta não exista.", email: "Email", savePassword: "Salvar senha", saving: "Salvando…", sendLink: "Enviar link", sending: "Enviando…", backToAccess: "Voltar ao acesso",
      extraVerification: "Verificação adicional", completeSecondFactor: "Conclua o segundo fator para continuar.", recoveryCode: "Código de recuperação", authenticationCode: "Código de autenticação", verify: "Verificar", useAuthenticator: "Usar autenticador", useRecoveryCode: "Usar código de recuperação", access: "Acesso", accessDescription: "Entre para consultar seu portal.", password: "Senha", signingIn: "Entrando…", signIn: "Entrar", forgotPassword: "Esqueceu sua senha?", publicBookingTitle: "Reserve seu próximo atendimento", portalDescription: "Portal do cliente e acesso a reservas incorporáveis.", services: "Serviços", noServices: "Não há serviços disponíveis.", schedule: "Agendar", widgetDescription: "O widget oferece retenção temporária e confirmação segura.", openingActivation: "Abrindo a ativação no Backoffice…", openingOperations: "Abrindo seu espaço operacional no Backoffice…"
    }
  }
};

export function publicExperienceCopy(locale: string | null | undefined): PublicExperienceCopy {
  return copies[normalizePortalLocale(locale)];
}

export function portalCopy(locale: string | null | undefined): PublicExperienceCopy & { customer: CustomerCopy; provider: ProviderCopy; auth: AuthCopy } {
  const normalized = normalizePortalLocale(locale);
  return { ...copies[normalized], customer: customerCopies[normalized], provider: providerCopies[normalized], auth: authCopies[normalized] };
}
